import dayjs from 'dayjs';
import Vue from 'vue';
import { isEqual, debounce } from 'lodash';
import { fetchDataQueryList, splitQuery, getDataRows, getDateRange, generateCSV } from './EmergentConcepts.utils';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import SegmentationChart from 'components/charts/SegmentationChart/SegmentationChart.vue';
import Tooltip from 'components/DataWidgets/EmergentConcepts/Tooltip.vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import icon from 'assets/img/dashboards/dash-emerging.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import { expandQuery } from 'src/utils/query';
import ChartUtils from 'src/utils/chart';
import { makeBarChartSlide } from '../DataWidgetUtils';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
export default Vue.extend({
    components: {
        WidgetMessagePanel,
        DownloadExportButton,
        SegmentationChart,
        WidgetFrame,
        WidgetMenu,
        Tooltip,
    },
    props: {
        exportName: { type: String, required: false, default: '' },
        devMode: { type: Boolean, required: false, default: false },
        zoomToRoute: { type: Object, required: false, default: null },
        isZoomed: { type: Boolean, required: false, default: false },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        baseQuery: { type: Array, required: true },
        dateFields: { type: Array, required: true },
        topicId: { type: String, required: true },
        projectId: { type: Number, required: true },
        chrysalisRef: { type: String, required: true },
        chrysalisUrl: { type: String, required: true },
        savedQueries: { type: Array, required: true },
        defaultDateField: { type: String, required: true },
        showDrilldown: { type: Boolean, required: true },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        groupbyNotSupported: { type: Boolean, default: false },
    },
    data() {
        return {
            icon,
            errorIcon,
            isLoading: false,
            hasErrored: false,
            width: 0,
            height: 0,
            conceptList: [],
            tooltipStats: null,
            headings: [],
            sortSelection: 'freq_diff_fraction',
            sortAsc: false,
            customDateRange: [undefined, undefined],
            dateField: this.defaultDateField || this.dateFields[0].name,
            displaySelection: 'Frequency change',
            fromSelection: 'Past 30 days',
            minConceptFreq: 0,
        };
    },
    computed: {
        hasValidData() {
            return this.dataRows.length > 0;
        },
        minValue() {
            return this.sortSelection === 'freq_diff' ? 0 : -1;
        },
        dataRows() {
            return getDataRows(this.conceptList, this.sortSelection, this.displaySelection === 'Frequency change'
                ? 'freq_diff_fraction'
                : 'reldiff');
        },
        menus() {
            return [
                {
                    name: 'Display',
                    selection: this.displaySelection,
                    options: [[{
                                type: 'menu',
                                options: ['Frequency change', 'Relative difference'],
                                showSelected: true,
                                selected: this.displaySelection
                            }]]
                },
                {
                    name: 'Min Concept Freq',
                    selection: this.minConceptFreq,
                    options: [[{
                                type: 'inputNumber',
                                // Without having something in the options list,
                                // the dropdown shows "No Data"
                                options: ['inputNumber'],
                                settings: {
                                    min: 0,
                                    step: 10,
                                    max: 100
                                },
                                showSelected: true,
                                selected: this.minConceptFreq
                            }]]
                },
                {
                    name: 'From',
                    selection: this.fromSelectionDisplayFormat(this.fromSelection),
                    options: [
                        [{
                                title: 'From',
                                type: 'radio',
                                options: [
                                    { label: 'Past 7 days', value: 'Past 7 days' },
                                    { label: 'Past 30 days', value: 'Past 30 days' },
                                    { label: 'Past 90 days', value: 'Past 90 days' },
                                    { label: 'Past 365 days', value: 'Past 365 days' },
                                    {
                                        label: 'Custom range...',
                                        value: 'Custom',
                                        input: 'daterange',
                                        inputValue: this.customDateRange,
                                        onChange: this.setDateRange,
                                    },
                                ],
                                showSelected: true,
                                selected: [this.fromSelection]
                            }, {
                                title: 'Date Field',
                                type: 'radio',
                                options: this.dateFields.map(({ name }) => name),
                                showSelected: true,
                                selected: [this.dateField]
                            }],
                    ]
                },
            ];
        },
        dateRange() {
            return getDateRange(this.fromSelection, this.customDateRange);
        },
        queryHalves() {
            const [startDate, endDate] = this.dateRange;
            const queries = this.baseQuery.map((query) => {
                return expandQuery('Other', query, this.savedQueries);
            });
            return splitQuery(queries, startDate, endDate, this.dateField);
        },
    },
    watch: {
        config: {
            deep: true,
            handler() {
                this.setOptionsFromConfig();
            }
        },
        baseQuery: {
            deep: true,
            handler(newVal, oldVal) {
                if (!isEqual(oldVal, newVal)) {
                    this.fetchData();
                }
            }
        },
        fromSelection(val) {
            if (val !== 'Custom' || this.customDateRange.every((d) => !!d)) {
                this.fetchData();
            }
        },
        sortSelection() {
            this.fetchData();
        },
        sortAsc() {
            this.fetchData();
        },
        isZoomed() {
            this.fetchData();
        },
        dateField() {
            this.fetchData();
        },
        customDateRange(val) {
            if (val[0] && val[1]) {
                this.fetchData();
            }
        },
        displaySelection(display) {
            this.setHeadings(display);
        },
        minConceptFreq: debounce(function () { this.fetchData(); }, 500),
    },
    mounted() {
        this.setOptionsFromConfig();
        this.setHeadings(this.displaySelection);
        this.fetchData();
    },
    methods: {
        updateConfig() {
            const updated = Object.assign({}, this.config, {
                options: {
                    customDateRange: this.customDateRange,
                    display: this.displaySelection,
                    dateField: this.dateField,
                    from: this.fromSelection,
                    minConceptFreq: this.minConceptFreq,
                }
            });
            this.$emit('config-changed', updated);
        },
        setOptionsFromConfig() {
            this.customDateRange = this.config?.options?.customDateRange ?? [undefined, undefined];
            this.displaySelection = this.config?.options?.display ?? 'Frequency change';
            this.fromSelection = this.config?.options?.from ?? 'Past 30 days';
            this.dateField = this.config?.options?.dateField ?? (this.defaultDateField || this.dateFields[0].name);
            this.minConceptFreq = this.config?.options?.minConceptFreq ?? 30;
        },
        setDateRange(dates) {
            this.customDateRange = dates;
            this.updateConfig();
        },
        setHeadings(display) {
            const headings = [
                { label: 'Concept', sortable: false },
                { label: 'Freq (#) change', sortable: true, sortAsc: null, key: 'freq_diff' },
            ];
            display === 'Frequency change' && headings.push({ label: 'Freq (%) change', sortable: true, sortAsc: false, key: 'freq_diff_fraction' });
            display === 'Relative difference' && headings.push({ label: 'Relative diff', sortable: true, sortAsc: false, key: 'reldiff' });
            this.headings = headings;
            this.sortSelection = headings.slice(-1)[0].key || 'freq_diff';
            this.sortAsc = headings.slice(-1)[0].sortAsc || false;
        },
        onHeadingClick(headingIndex) {
            const heading = this.headings[headingIndex];
            const { sortAsc } = heading;
            // Reset and toggle sort mode
            this.headings.slice(1).forEach((h) => h.sortAsc = null);
            this.sortAsc = heading.sortAsc = sortAsc == null ? false : !sortAsc;
            if (heading.key) {
                this.sortSelection = heading.key;
            }
            this.$analytics.track.emergent.changeSort(this.sortSelection, this.sortAsc ? 'asc' : 'desc');
        },
        setTooltipStats(index) {
            const concept = this.conceptList[index];
            this.tooltipStats = {
                freq_percent_1: ChartUtils.formatPercent(concept.freq_fraction_1 * 100),
                freq_percent_2: ChartUtils.formatPercent(concept.freq_fraction_2 * 100),
                freq_percent_total: ChartUtils.formatPercent(concept.freq_fraction_total * 100),
                freq_1: concept.freq_1,
                freq_2: concept.freq_2,
                freq_total: concept.freq_total,
                freq_diff_percent: ChartUtils.formatPercent(concept.freq_diff_fraction * 100),
                freq_diff: concept.freq_diff,
                daysAgo: this.fromSelection.match(/\d+/)?.[0] || '',
                label: concept.concept_name,
                reldiff: ChartUtils.formatPercent(concept.reldiff * 100),
                customDates: this.customDateRange,
            };
        },
        setChartDimensions(width, height) {
            this.width = width;
            this.height = height;
        },
        async fetchData() {
            this.isLoading = true;
            this.hasErrored = false;
            try {
                const [base_list, query_list] = this.queryHalves;
                const { payload } = await fetchDataQueryList({
                    base_list,
                    query_list,
                    chrysalisRef: this.chrysalisRef,
                    chrysalisURL: this.chrysalisUrl,
                    limit: this.isZoomed ? 30 : 10,
                    baselineListOperation: 'intersection',
                    queryListOperation: 'intersection',
                    sortField: this.sortSelection,
                    projectId: this.projectId,
                    topicId: this.topicId,
                    sortAsc: this.sortAsc,
                    minConceptFreq: this.minConceptFreq
                });
                this.conceptList = payload;
                this.$analytics.track.emergent.load(this.conceptList.length);
            }
            catch (e) {
                this.hasErrored = true;
            }
            finally {
                this.isLoading = false;
            }
        },
        setMenuSelection(name, [title, val]) {
            if (name === 'Display') {
                this.displaySelection = val;
                this.$analytics.track.emergent.changeDisplay(this.displaySelection);
            }
            if (name === 'From') {
                if (title === 'Date Field') {
                    this.dateField = val;
                }
                if (title === 'From') {
                    this.fromSelection = val;
                }
                this.$analytics.track.emergent.changeRange(this.dateField, this.fromSelection);
            }
            if (name === 'Min Concept Freq') {
                this.minConceptFreq = val;
            }
            this.updateConfig();
        },
        fromSelectionDisplayFormat(fromSelection) {
            function customDateFmt(dt) {
                return dt == null ? 'No date set' : dayjs(dt, 'DD/MM/YYYY').format('D MMM, YYYY');
            }
            if (fromSelection === 'Custom') {
                return `${customDateFmt(this.customDateRange[0])} \u2014 ${customDateFmt(this.customDateRange[1])}`;
            }
            return fromSelection;
        },
        getCsvData() {
            return generateCSV(this.conceptList, this.displaySelection);
        },
        getChartEl() {
            return this.$el.querySelector('div.content');
        },
        getSvgExportConfig() {
            const el = this.getChartEl();
            if (!el)
                return;
            const bb = el.getBoundingClientRect();
            return { dims: { height: bb.height, width: bb.width }, css: `
        svg {
          top: 0;
          left: 0;
        }
        .chart {
          background-color: #fff;
          cursor: default;
        }
        text.first {
          text-anchor: start;
        }
        text.end{
          text-anchor: end;
        }
        rect.bar{
            fill: #11ACDF;
        }
        rect.background {
          fill: #F1F1F1;
        }
        rect.indicator {
          fill: #8064AA;
        }
        text.sorted {
          fill: #068CCC;
          font-weight: bold;
        }
        rect.legend-icon-one {
          fill: #11ACDF;
        }
        rect.legend-icon-two {
          fill: #8064AA;
        }
        .legend {
          cursor: default;
        }`
            };
        },
        rowClicked(rowIndex) {
            if (!this.showDrilldown)
                return;
            const concept = this.dataRows[rowIndex]?.label;
            this.$emit('concept-clicked', concept);
        },
        // error panel items
        refresh() {
            window.location.reload();
        },
        reload() {
            this.fetchData();
        },
        contact() {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        },
        makePptSlide(pptx) {
            if (!this.dataRows.length)
                return;
            const slide = pptx.addSlide();
            makeBarChartSlide(pptx, slide, [{
                    name: 'Emergent Concepts',
                    labels: this.dataRows.map((r) => r.label),
                    values: this.dataRows.map((r) => r.columns[0].value),
                }], `${this.exportName} - Emergent Concepts`, 'Frequency Change', '', { showValue: true });
        },
    }
});
